'use client';
import { useCallback, useEffect, useState } from 'react';
import { fetchWithCache, invalidateResource } from '@/lib/use-resource';
import { AppNav } from '@/components/AppNav';
import { compositeScore } from '@/lib/composite-score';
import { SECTOR_COLORS } from '@/lib/sector-colors';
import { firstWord } from './_components/utils';
import type { ApiStock, ApiPortfolio, ModelStats, ScorecardRow } from './_components/types';
import { PresentationActions } from './_components/PresentationActions';
import { Slide01Cover } from './_components/slides/Slide01Cover';
import { Slide02Objective } from './_components/slides/Slide02Objective';
import { Slide03Committed } from './_components/slides/Slide03Committed';
import { Slide04Ackman } from './_components/slides/Slide04Ackman';
import { SlideThisPresentation } from './_components/slides/SlideThisPresentation';
import { Slide04FourTools } from './_components/slides/Slide04FourTools';
import { Slide04bMicrosoftExample } from './_components/slides/Slide04bMicrosoftExample';
import { Slide05UniverseExpansion } from './_components/slides/Slide05UniverseExpansion';
import { Slide06ProcessFunnel } from './_components/slides/Slide06ProcessFunnel';
import { Slide07FactorModel } from './_components/slides/Slide07FactorModel';
import { Slide08Monitoring } from './_components/slides/Slide08Monitoring';
import { Slide09ResearchAnalyst } from './_components/slides/Slide09ResearchAnalyst';
import { Slide10HedgeLab } from './_components/slides/Slide10HedgeLab';
import { Slide11OpportunityZone } from './_components/slides/Slide11OpportunityZone';
import { Slide12Rating } from './_components/slides/Slide12Rating';
import { Slide13ModelAudit } from './_components/slides/Slide13ModelAudit';
import { SlideWhatItMeans } from './_components/slides/SlideWhatItMeans';
import { Slide14Scorecard } from './_components/slides/Slide14Scorecard';
import { Slide15AlphaZoneInPortfolio } from './_components/slides/Slide15AlphaZoneInPortfolio';
import { Slide17Team } from './_components/slides/Slide17Team';
import { Slide18FundTerms } from './_components/slides/Slide18FundTerms';

export default function PresentationPage() {
  const [stocks, setStocks] = useState<Map<string, ApiStock> | null>(null);
  const [portfolio, setPortfolio] = useState<ApiPortfolio | null>(null);
  const [stats, setStats] = useState<ModelStats | null>(null);
  const [alphaSymbols, setAlphaSymbols] = useState<Set<string>>(new Set());
  const [scorecardUpdatedAt, setScorecardUpdatedAt] = useState<Date | null>(null);
  const [scorecardRefreshing, setScorecardRefreshing] = useState(false);
  const [, setNowTick] = useState(0);

  // Pull APIs via the shared cache. Other consumers (HoldingsList, ScatterPlot3D,
  // etc.) hit the same endpoints — useResource cache means one fetch satisfies all.
  // /api/alpha-basket returns { basket: { slots }, aggregates, events } — must read
  // basket.slots, not slots directly (feedback_alpha_basket_envelope_unwrap.md).
  const fetchScorecard = useCallback(async () => {
    const [sRes, p, ms, ab] = await Promise.all([
      fetchWithCache<{ stocks?: ApiStock[]; count?: number }>('/api/stocks').catch(() => ({}) as { stocks?: ApiStock[]; count?: number }),
      fetchWithCache<ApiPortfolio>('/api/portfolio').catch(() => null as ApiPortfolio | null),
      fetchWithCache<ModelStats>('/api/model-stats').catch(() => null as ModelStats | null),
      fetchWithCache<{ basket?: { slots?: Array<{ current?: { symbol?: string } }> } }>('/api/alpha-basket').catch(() => ({}) as { basket?: { slots?: Array<{ current?: { symbol?: string } }> } }),
    ]);
    const slots = ab?.basket?.slots ?? [];
    const alphaSet = new Set<string>();
    for (const slot of slots) {
      const sym = slot?.current?.symbol;
      if (sym) alphaSet.add(sym);
    }
    setAlphaSymbols(alphaSet);
    const m = new Map<string, ApiStock>();
    for (const s of sRes?.stocks ?? []) m.set(s.symbol, s);
    setStocks(m);
    setPortfolio(p);
    setStats({
      universeCount: ms?.universeCount ?? sRes?.count ?? 1136,
      factorCount: ms?.factorCount ?? 39,
      qualityFactorCount: ms?.qualityFactorCount ?? 27,
      valuationFactorCount: ms?.valuationFactorCount ?? 12,
      pillarWeights: ms?.pillarWeights,
      pillars: ms?.pillars,
      valuation: ms?.valuation,
      quality: ms?.quality,
    });
    setScorecardUpdatedAt(new Date());
  }, []);

  useEffect(() => { fetchScorecard(); }, [fetchScorecard]);

  // Auto-refresh prices (FMP bulk quote, ~1s) every 60 seconds, then re-pull APIs
  // so slide 11 stays live with intraday market moves.
  useEffect(() => {
    const id = setInterval(async () => {
      setScorecardRefreshing(true);
      try {
        await fetch('/api/refresh-prices', { method: 'POST' });
        // Server rewrote scores + portfolio; drop cached GETs so the
        // next fetchScorecard call hits the network.
        invalidateResource('/api/stocks');
        invalidateResource('/api/portfolio');
        invalidateResource('/api/alpha-basket');
        await fetchScorecard();
      } finally {
        setScorecardRefreshing(false);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [fetchScorecard]);

  // 1Hz tick so the "X seconds ago" label re-renders.
  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Build scorecard rows on the fly
  const rows: ScorecardRow[] = (() => {
    if (!stocks || !portfolio) return [];
    const weights = portfolio.weights ?? {};
    return portfolio.symbols.map(sym => {
      const s = stocks.get(sym);
      const q = s?.qualityScore ?? null;
      const v = s?.valuationScore ?? null;
      return {
        symbol: sym,
        company: firstWord(sym, s?.name),
        weightPct: weights[sym] != null ? weights[sym] * 100 : null,
        quality: q,
        valuation: v,
        profitability: s?.pillarScores?.profitability ?? null,
        growth: s?.pillarScores?.growth ?? null,
        safety: s?.pillarScores?.safety ?? null,
        payout: s?.pillarScores?.shareholderReturns ?? null,
        composite: compositeScore(q, v),
      };
    }).sort((a, b) => (b.weightPct ?? 0) - (a.weightPct ?? 0));
  })();
  const cashPct = (portfolio?.cashWeight ?? 0) * 100;

  function wavg(getter: (r: ScorecardRow) => number | null): number | null {
    let sumW = 0, sumWX = 0;
    for (const r of rows) {
      const w = r.weightPct;
      const x = getter(r);
      if (w == null || x == null) continue;
      sumW += w; sumWX += w * x;
    }
    return sumW > 0 ? sumWX / sumW : null;
  }
  const portfolioAvg = {
    profitability: wavg(r => r.profitability),
    growth: wavg(r => r.growth),
    safety: wavg(r => r.safety),
    payout: wavg(r => r.payout),
    quality: wavg(r => r.quality),
    valuation: wavg(r => r.valuation),
    composite: wavg(r => r.composite),
  };

  const universe = stats?.universeCount ?? 1136;
  const factorCount = stats?.factorCount ?? 39;

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#e2e8f0]">
      <AppNav />
      <div className="max-w-[1320px] mx-auto p-6 print:p-0 print:max-w-none">
        <header className="mb-6 flex items-start justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl font-bold text-white">Presentation</h1>
            <p className="text-[17px] text-[#94a3b8] mt-1">
              Live deck. 21 slides, regenerated from current data each load. Update refreshes the visualizer screenshot. Download PDF builds a fresh, printable PDF.
            </p>
          </div>
          <PresentationActions />
        </header>

        <Slide01Cover />
        <Slide02Objective />
        <Slide03Committed />
        <Slide04Ackman />
        <SlideThisPresentation pageNum={5} />
        <Slide11OpportunityZone universe={universe} factorCount={factorCount} />
        <Slide04FourTools universe={universe} />
        <Slide05UniverseExpansion universe={universe} />
        <Slide06ProcessFunnel universe={universe} />
        <Slide07FactorModel stats={stats} />
        <Slide08Monitoring universe={universe} />
        <Slide09ResearchAnalyst universe={universe} />
        <Slide10HedgeLab />
        <Slide04bMicrosoftExample />
        <Slide12Rating universe={universe} />
        <Slide13ModelAudit universe={universe} />
        <SlideWhatItMeans universe={universe} />
        <Slide14Scorecard
          rows={rows}
          cashPct={cashPct}
          portfolioAvg={portfolioAvg}
          universe={universe}
          scorecardUpdatedAt={scorecardUpdatedAt}
          scorecardRefreshing={scorecardRefreshing}
          alphaSymbols={alphaSymbols}
        />
        <Slide15AlphaZoneInPortfolio />
        <Slide17Team />
        <Slide18FundTerms />

        <style jsx global>{`
          @media print {
            @page { size: 13.333in 7.5in; margin: 0; }
            body { background: #121722 !important; }
            .slide { width: 100vw !important; height: 100vh !important; max-width: none !important; aspect-ratio: auto !important; margin: 0 !important; page-break-after: always; }
            .slide:last-of-type { page-break-after: avoid !important; break-after: avoid !important; }
          }
        `}</style>

        {/* Reuse warning to silence — sectorColors imported but only used as a token */}
        <div className="hidden">{Object.keys(SECTOR_COLORS).length}</div>
      </div>
    </div>
  );
}
